/**
 * App Context
 * Global state management for the application
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import s3Service from '../services/s3Service';
import { TORAH_ORDER, TEMP_CARDS } from '../consts/constTora';

const AppContext = createContext();

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [manifest, setManifest] = useState(null);
  const [books, setBooks] = useState(TEMP_CARDS);
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedParsha, setSelectedParsha] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [content, setContent] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [fontSize, setFontSize] = useState(110);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  // Fetch manifest on mount
  useEffect(() => {
    const loadManifest = async () => {
      setLoading(true);
      try {
        const manifestData = await s3Service.fetchManifest();
        setManifest(manifestData);
        
        const booksData = s3Service.getBooksFromManifest(manifestData);
        booksData.sort((a, b) => TORAH_ORDER.indexOf(a.key) - TORAH_ORDER.indexOf(b.key));
        if(booksData){
          setBooks(booksData);
        }
      } catch (err) {
        setError('Failed to load manifest');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadManifest();
  }, []);

  // Fetch content when file is selected
  useEffect(() => {
    if (selectedBook && selectedParsha && selectedFile) {
      const loadContent = async () => {
        setLoading(true);
        setError(null);
        try {
          const html = await s3Service.fetchFileContent(
            selectedBook.key,
            selectedParsha.key,
            selectedFile
          );
          setContent(html);
        } catch (err) {
          setError('Failed to load content');
          setContent('<p style="text-align: right;">שגיאה בטעינת התוכן</p>');
        } finally {
          setLoading(false);
        }
      };
      
      loadContent();
    }
  }, [selectedBook, selectedParsha, selectedFile]);

  // Search function
  const performSearch = async (query, page = 1) => {
  if (!query.trim()) return;

  setIsSearching(true);
  setSearchResults([]);
  setError(null);

  try {
    const { results, totalResults, totalPages: pages } = await s3Service.searchContent(query, page, 20);
    
    setSearchResults(results);
    setCurrentPage(page);
    setTotalPages(pages);
    setTotalResults(totalResults);
  } catch (err) {
    setError('Search failed');
    console.error(err);
  } finally {
    setIsSearching(false);
  }
};

  // Download PDF function
  const downloadPDF = () => {
    if (!selectedBook || !selectedParsha || !selectedFile) return;
    
    const pdfUrl = s3Service.getPDFUrl(selectedBook.key, selectedParsha.key, selectedFile);
    window.open(pdfUrl, '_blank');
  };

  // Reset selection
  const resetSelection = () => {
    setSelectedBook(null);
    setSelectedParsha(null);
    setSelectedFile(null);
    setContent('');
    setSearchResults([]);
    setSearchQuery('');
    setCurrentPage(1);
    setTotalPages(1);
    setTotalResults(0);
  };

  // Navigate back
  const goBack = () => {
    if (selectedFile) {
      setSelectedFile(null);
      setContent('');
    } else if (selectedParsha) {
      setSelectedParsha(null);
    } else if (selectedBook) {
      setSelectedBook(null);
    }
  };

  const value = {
    manifest,
    books,
    selectedBook,
    setSelectedBook,
    selectedParsha,
    setSelectedParsha,
    selectedFile,
    setSelectedFile,
    content,
    searchQuery,
    setSearchQuery,
    searchResults,
    isSearching,
    currentPage,
    totalPages,
    totalResults,
    darkMode,
    setDarkMode,
    fontSize,
    setFontSize,
    loading,
    error,
    performSearch,
    downloadPDF,
    resetSelection,
    goBack
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};